//External libs
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';

//Local libs
import Service from 'src/app/common/service';
import CustomerDeviceModel from './customerDevice.model';

/**
 * @service CustomerDeviceService
 * @description Service for managing the relationship between customers and devices.
 * This service provides HTTP methods to count, retrieve, and map IoT devices
 * to specific business entities or farms within the AquaCloud ecosystem. It handles
 * operations like fetching device counts, listing mapped and unmapped devices, and
 * creating new device-to-customer mappings.
 * Extends the base Service class with the generic type CustomerDeviceModel.
 *
 * @feature 1. Get total device count for a business.
 * @feature 2. Retrieve all devices mapped to a specific farm.
 * @feature 3. List all devices that are not yet mapped to any customer.
 * @feature 4. Map an unassigned device to a customer or farm.
 * @feature 5. Cancel ongoing HTTP requests to prevent memory leaks.
 *
 * @architecture
 * Extends the base `Service<CustomerDeviceModel>` to provide standardized REST API
 * integration with the `/aqu/customerdevice/` endpoint. It uses Angular's `HttpClient`
 * for all HTTP operations and manages subscriptions in an `ongoingRequests` array
 * for easy cancellation, ensuring robust resource management.
 *
 * @service-endpoint /aqu/customerdevice/
 * @service-provider root (singleton, application-wide)
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Injectable({
  providedIn: 'root'
})
export class CustomerDeviceService extends Service<CustomerDeviceModel> {

  /**
   * @type {Array<Subscription>}
   * @description Array tracking all ongoing HTTP request subscriptions.
   * This is used to manage and cancel pending requests, for example, when a component is destroyed.
   * @default []
   * @side-effect Stores subscription references, which are later used by `cancelOnGoingHttpRequests()`.
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * @method constructor
   * @description Service constructor that injects the HttpClient and sets up the service endpoint.
   *
   * Calls the parent `Service` constructor, passing the `HttpClient` instance and the
   * specific API endpoint path for customer-device operations.
   *
   * @param {HttpClient} httpClient - @service Angular's HTTP client for making API requests.
   * @returns {void}
   */
  constructor(public httpClient: HttpClient) {
    super(httpClient, '/aqu/customerdevice/');
  }

  public getDevicesBuSelectedType(device: CustomerDeviceModel) {
    return new Promise<Array<CustomerDeviceModel>>((resolve, reject) => {
      const request = this.httpClient.post<Array<CustomerDeviceModel>>(`${this.baseUrl}${this.serviceUrl}devicesDetails/businessID`, device, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  public getCustomerDevices(businessIds: Array<string>) {
    return new Promise<Array<CustomerDeviceModel>>((resolve, reject) => {
      const request = this.httpClient.post<Array<CustomerDeviceModel>>(`${this.baseUrl}${this.serviceUrl}businessID`, {
        businessId: businessIds
      }, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }
  
  public assignDeviceToFarmId(device: CustomerDeviceModel) {
    return new Promise<Array<CustomerDeviceModel>>((resolve, reject) => {
      const request = this.httpClient.post<Array<CustomerDeviceModel>>(`${this.baseUrl}${this.serviceUrl}deviceId/businessId`, device, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getAlladdMapDeviceWithFarm
   * @async
   * @description Retrieves all devices that are mapped to a specific farm or business.
   *
   * Makes an HTTP GET request to the `/businessID/{businessId}` endpoint to fetch a list
   * of all devices currently associated with the given business.
   *
   * @param {string} businessId - The unique identifier for the business/farm.
   * @returns {Promise<Array<CustomerDeviceModel>>} A promise that resolves with an array of mapped devices.
   * @throws {HttpErrorResponse} Rejects the promise if the API call fails.
   * @side-effect Creates an HTTP GET request and adds its subscription to the `ongoingRequests` array.
   *
   * @example
   * const mappedDevices = await customerDeviceService.getAlladdMapDeviceWithFarm('farm-456');
   * console.log(mappedDevices);
   */
  public getAlladdMapDeviceWithFarm(businessId: string) {
    return new Promise<Array<CustomerDeviceModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<CustomerDeviceModel>>(`${this.baseUrl}${this.serviceUrl}businessID/${businessId}`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getAllNonMapped
   * @async
   * @description Fetches all devices that are currently not mapped to any customer or farm.
   *
   * This is useful for administrative views where unassigned devices need to be listed
   * for assignment. Makes an HTTP GET request to the `device/nonMapped` endpoint.
   *
   * @returns {Promise<CustomerDeviceModel>} A promise that resolves with a list of unmapped devices.
   * Note: The return type is `CustomerDeviceModel`, but the endpoint likely returns an array.
   * @throws {HttpErrorResponse} Rejects the promise if the API call fails.
   * @side-effect Creates an HTTP GET request and adds its subscription to the `ongoingRequests` array.
   *
   * @example
   * const unmappedDevices = await customerDeviceService.getAllNonMapped();
   * // unmappedDevices is expected to be an array of devices
   */
  public getAllNonMapped() {
    return new Promise<CustomerDeviceModel>((resolve, reject) => {
      const request = this.httpClient.get<CustomerDeviceModel>(`${this.baseUrl}${this.serviceUrl}device/nonMapped`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method mapDevice
   * @async
   * @description Maps a device to a customer or farm.
   *
   * Sends a `CustomerDeviceModel` object containing the device ID and the target
   * customer/farm information to the `mapDevice` endpoint via an HTTP POST request.
   * This creates the association in the backend.
   *
   * @param {CustomerDeviceModel} device - The device model containing mapping information.
   * @returns {Promise<CustomerDeviceModel>} A promise that resolves with the updated device mapping details.
   * @throws {HttpErrorResponse} Rejects the promise if the API call fails.
   * @side-effect Creates a new device-customer mapping in the database. Adds the request subscription to `ongoingRequests`.
   *
   * @example
   * const deviceToMap = new CustomerDeviceModel();
   * deviceToMap.device_id = 'device-xyz';
   * deviceToMap.business_id = 'business-123';
   * const mappedDevice = await customerDeviceService.mapDevice(deviceToMap);
   * console.log('Successfully mapped:', mappedDevice);
   */
  public mapDevice(device: CustomerDeviceModel) {
    return new Promise<CustomerDeviceModel>((resolve, reject) => {
      const request = this.httpClient.post<CustomerDeviceModel>(`${this.baseUrl}${this.serviceUrl}mapDevice`, device, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method cancelOnGoingHttpRequests
   * @description Cancels all ongoing/pending HTTP requests initiated by this service.
   *
   * Iterates through the `ongoingRequests` array and unsubscribes from each active
   * subscription. This is a crucial cleanup step to prevent memory leaks and
   * race conditions when a component is destroyed.
   *
   * @execution-flow
   * 1. Iterate through each subscription in the `ongoingRequests` array.
   * 2. Check if the subscription object is valid and has an `unsubscribe` function.
   * 3. Call `unsubscribe()` on each active subscription to abort the HTTP request.
   * 4. Clear the `ongoingRequests` array.
   *
   * @returns {void}
   * @side-effect
   * - Aborts all pending HTTP requests made by this service instance.
   * - Clears the `ongoingRequests` array.
   *
   * @usage This method should be called in the `ngOnDestroy` lifecycle hook of any
   * component that uses this service to ensure proper cleanup.
   * @example
   * ngOnDestroy() {
   *   this.customerDeviceService.cancelOnGoingHttpRequests();
   * }
   */
   public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach((request) => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
